import { useState, useEffect, useCallback } from 'react';
import { useBusiness } from '../contexts/BusinessContext';
import api from '../api';
import { toast } from 'sonner';
import { X, Plus, Pin, Trash2, FileText } from 'lucide-react';

export default function NotesPanel({ isOpen, onClose }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [content, setContent] = useState('');
  const { activeBusiness, businesses, getBusinessColor, getBusinessShortCode } = useBusiness();

  const fetchNotes = useCallback(async (signal) => {
    setLoading(true);
    try {
      const params = activeBusiness ? { businessId: activeBusiness.id } : {};
      const res = await api.get('/notes', { params, signal });
      setNotes(res.data);
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
      // fetch failed;
    } finally {
      setLoading(false);
    }
  }, [activeBusiness]);

  useEffect(() => {
    if (isOpen) fetchNotes();
  }, [isOpen, fetchNotes]);

  const handleCreate = async () => {
    try {
      const res = await api.post('/notes', {
        content: '',
        businessId: activeBusiness?.id || businesses[0]?.id || null,
        pinned: false
      });
      setNotes((prev) => [res.data, ...prev]);
      setEditId(res.data.id);
      setContent('');
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
      toast.error('Could not create note');
    }
  };

  const handleSave = async (noteId) => {
    try {
      await api.put(`/notes/${noteId}`, { content });
      setNotes((prev) => prev.map((note) => note.id === noteId ? { ...note, content } : note));
      setEditId(null);
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
      toast.error('Could not save note');
    }
  };

  const handlePin = async (note) => {
    try {
      await api.put(`/notes/${note.id}`, { pinned: !note.pinned });
      setNotes((prev) => prev.map((entry) => entry.id === note.id ? { ...entry, pinned: !entry.pinned } : entry));
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
      toast.error('Could not pin note');
    }
  };

  const handleDelete = async (noteId) => {
    try {
      await api.delete(`/notes/${noteId}`);
      setNotes((prev) => prev.filter((note) => note.id !== noteId));
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
      toast.error('Could not delete note');
    }
  };

  const pinnedNotes = notes.filter((note) => note.pinned && !note.archived);
  const otherNotes = notes.filter((note) => !note.pinned && !note.archived);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-40 flex justify-end" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} style={{ background: 'var(--pd-overlay)', backdropFilter: 'blur(4px)' }}>
      <div data-testid="notes-panel" className="w-full sm:w-[390px] h-full flex flex-col animate-slide-up" style={{ background: 'var(--pd-popover)', borderLeft: '1px solid var(--pd-popover-border)', boxShadow: '-14px 0 32px rgba(0,0,0,0.32)' }}>
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'var(--pd-border)' }}>
          <div>
            <h3 className="text-base font-semibold" style={{ letterSpacing: '-0.03em', color: 'var(--pd-text)' }}>Quick Notes</h3>
            <p className="text-[11px] mt-1" style={{ color: 'var(--pd-subtle)' }}>Capture context without leaving your current screen.</p>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="notes-add-button" onClick={handleCreate} className="pd-icon-button" style={{ width: 38, height: 38 }}><Plus className="w-4 h-4" /></button>
            <button data-testid="notes-close-button" onClick={onClose} className="pd-icon-button" style={{ width: 38, height: 38 }}><X className="w-4 h-4" /></button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
          {loading ? (
            <div className="space-y-2">{[1,2,3].map((i) => <div key={i} className="h-16 rounded-2xl skeleton-shimmer" />)}</div>
          ) : notes.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-center">
              <FileText className="w-10 h-10 mb-3" style={{ color: 'var(--pd-subtle)' }} />
              <div className="text-sm font-medium">No notes yet</div>
              <button onClick={handleCreate} className="text-xs mt-2" style={{ color: 'var(--pd-focus)' }}>Create one</button>
            </div>
          ) : (
            <>
              {pinnedNotes.length > 0 ? (
                <div className="mb-4">
                  <div className="text-[10px] uppercase tracking-wider font-medium mb-2 flex items-center gap-1" style={{ color: 'var(--pd-subtle)' }}><Pin className="w-3 h-3" /> Pinned</div>
                  {pinnedNotes.map((note) => <NoteCard key={note.id} note={note} editId={editId} content={content} setEditId={setEditId} setContent={setContent} handleSave={handleSave} handlePin={handlePin} handleDelete={handleDelete} getBusinessColor={getBusinessColor} getBusinessShortCode={getBusinessShortCode} />)}
                </div>
              ) : null}
              {otherNotes.map((note) => <NoteCard key={note.id} note={note} editId={editId} content={content} setEditId={setEditId} setContent={setContent} handleSave={handleSave} handlePin={handlePin} handleDelete={handleDelete} getBusinessColor={getBusinessColor} getBusinessShortCode={getBusinessShortCode} />)}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function NoteCard({ note, editId, content, setEditId, setContent, handleSave, handlePin, handleDelete, getBusinessColor, getBusinessShortCode }) {
  const bizColor = note.businessId ? getBusinessColor(note.businessId) : '#94A3B8';
  const bizCode = note.businessId ? getBusinessShortCode(note.businessId) : '';
  const isEditing = editId === note.id;

  return (
    <div className="rounded-2xl p-3 mb-2 group" style={{ background: 'var(--pd-panel)', border: '1px solid var(--pd-border)', borderLeft: `3px solid ${bizColor}` }}>
      {isEditing ? (
        <>
          <textarea value={content} onChange={(e) => setContent(e.target.value)} className="w-full text-sm outline-none resize-none min-h-[90px] rounded-xl p-2" style={{ color: 'var(--pd-text)', background: 'transparent' }} autoFocus />
          <div className="flex justify-end gap-2 mt-2">
            <button onClick={() => setEditId(null)} className="text-[10px]" style={{ color: 'var(--pd-muted)' }}>Cancel</button>
            <button onClick={() => handleSave(note.id)} className="text-[10px] font-medium" style={{ color: 'var(--pd-focus)' }}>Save</button>
          </div>
        </>
      ) : (
        <>
          <div className="text-sm cursor-pointer whitespace-pre-wrap" style={{ color: 'var(--pd-text)', minHeight: 20 }} onClick={() => { setEditId(note.id); setContent(note.content || ''); }}>
            {note.content || <span style={{ color: 'var(--pd-subtle)' }}>Empty note — click to edit</span>}
          </div>
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-2">{bizCode ? <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: `${bizColor}12`, color: bizColor }}>{bizCode}</span> : null}</div>
            <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 transition-opacity">
              <button onClick={() => handlePin(note)} className="p-1 rounded" style={{ color: note.pinned ? 'var(--pd-warning)' : 'var(--pd-subtle)' }}><Pin className="w-3 h-3" /></button>
              <button onClick={() => handleDelete(note.id)} className="p-1 rounded" style={{ color: 'var(--pd-crimson)' }}><Trash2 className="w-3 h-3" /></button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
